(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  ready(() => {
    const body = document.body;
    const header = document.querySelector('[data-site-header]');
    const mobileToggle = document.querySelector('[data-mobile-nav-toggle]');
    const mobileNavigation = document.querySelector('[data-mobile-navigation]');
    const mobilePanel = mobileNavigation?.querySelector('[data-mobile-navigation-panel]');
    const mobileLinks = mobileNavigation?.querySelectorAll('a');
    let lastFocusedElement = null;

    const updateHeader = () => {
      if (!header) return;
      header.classList.toggle('is-scrolled', window.scrollY > 12);
    };

    updateHeader();
    window.addEventListener('scroll', updateHeader, { passive: true });

    const getFocusableElements = () => {
      if (!mobilePanel) return [];
      return [...mobilePanel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')];
    };

    const closeMenu = ({ restoreFocus = true } = {}) => {
      if (!mobileToggle || !mobileNavigation) return;
      mobileToggle.setAttribute('aria-expanded', 'false');
      mobileNavigation.classList.remove('is-open');
      mobileNavigation.setAttribute('aria-hidden', 'true');
      body.classList.remove('menu-open');
      if (restoreFocus && lastFocusedElement instanceof HTMLElement) {
        lastFocusedElement.focus();
      }
    };

    const openMenu = () => {
      if (!mobileToggle || !mobileNavigation) return;
      lastFocusedElement = document.activeElement;
      mobileToggle.setAttribute('aria-expanded', 'true');
      mobileNavigation.classList.add('is-open');
      mobileNavigation.setAttribute('aria-hidden', 'false');
      body.classList.add('menu-open');
      const focusable = getFocusableElements();
      focusable[0]?.focus();
    };

    mobileToggle?.addEventListener('click', () => {
      const expanded = mobileToggle.getAttribute('aria-expanded') === 'true';
      expanded ? closeMenu() : openMenu();
    });

    mobileNavigation?.addEventListener('click', (event) => {
      if (event.target === mobileNavigation) closeMenu();
    });

    mobileLinks?.forEach((link) => {
      link.addEventListener('click', () => closeMenu({ restoreFocus: false }));
    });

    document.addEventListener('keydown', (event) => {
      if (!mobileNavigation?.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
        return;
      }

      if (event.key === 'Tab') {
        const focusable = getFocusableElements();
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth > 960 && mobileNavigation?.classList.contains('is-open')) {
        closeMenu({ restoreFocus: false });
      }
    });

    const revealElements = document.querySelectorAll('.reveal-on-scroll');
    if (revealElements.length) {
      if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        const observer = new IntersectionObserver((entries, revealObserver) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
        revealElements.forEach((element) => observer.observe(element));
      } else {
        revealElements.forEach((element) => element.classList.add('is-visible'));
      }
    }

    const anchorLinks = document.querySelectorAll('a[href^="#"], a[href^="/#"]');
    anchorLinks.forEach((link) => {
      link.addEventListener('click', (event) => {
        const rawHref = link.getAttribute('href');
        if (!rawHref || rawHref === '#') return;
        const hash = rawHref.includes('#') ? rawHref.slice(rawHref.indexOf('#')) : '';
        const target = hash ? document.querySelector(hash) : null;
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        if (history.pushState) history.pushState(null, '', hash);
      });
    });

    const trackedSections = [...document.querySelectorAll('main section[id]')];
    const navigationLinks = [...document.querySelectorAll('.site-nav__link[data-section-link]')];
    if (trackedSections.length && navigationLinks.length && 'IntersectionObserver' in window) {
      const navObserver = new IntersectionObserver((entries) => {
        const visibleEntry = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visibleEntry) return;
        navigationLinks.forEach((link) => {
          const linkHash = link.getAttribute('href')?.split('#')[1];
          link.classList.toggle('is-active', linkHash === visibleEntry.target.id);
        });
      }, { rootMargin: '-25% 0px -65% 0px', threshold: [0.01, 0.15, 0.35] });
      trackedSections.forEach((section) => navObserver.observe(section));
    }

    const successfulForm = document.querySelector('[data-contact-success]');
    if (successfulForm) {
      successfulForm.setAttribute('tabindex', '-1');
      successfulForm.focus({ preventScroll: true });
      successfulForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
})();
